import React from 'react';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { buildBreadcrumbList, buildServiceSchema } from '../lib/structuredData';
import { CheckIcon, ArrowRightIcon, MessageCircleIcon, PenToolIcon, CodeIcon, CheckCircleIcon, ServerIcon, TestTubeIcon, RocketIcon, ShieldIcon, UsersIcon, TrendingUpIcon, FileTextIcon, ShoppingCartIcon, LayoutIcon, StarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
type WebsiteType = {
  icon: React.ReactNode;
  title: string;
  scope: string;
  price: string;
  priceSuffix: string;
  details: string;
  highlight?: boolean;
};
export const WebsitesPage: React.FC = () => {
  const websiteTypes: WebsiteType[] = [{
    icon: <FileTextIcon className="h-6 w-6" data-id="element-2946" data-mp="jk63xgb0jiqm" />,
    title: 'Onepager',
    scope: "1 tot 3 secties op één pagina",
    price: 'vanaf €499',
    priceSuffix: 'eenmalig, excl. BTW',
    details: 'Een strakke, conversiegerichte onepager met één duidelijke boodschap. Geschikt voor zzp en kleine dienstverleners die snel online willen staan met een professioneel visitekaartje.',
    highlight: true
  }, {
    icon: <LayoutIcon className="h-6 w-6" data-id="element-2947" data-mp="3w13xybdgx3j0" />,
    title: 'Maatwerk website',
    scope: "4 of meer pagina's",
    price: 'Op aanvraag',
    priceSuffix: 'afgestemd op scope',
    details: 'Een volwaardige website met meerdere dienst-paginas, een blog of kennisbank, en ruimte voor SEO-uitbreiding. Voor bedrijven die hun complete dienstverlening online willen presenteren.'
  }, {
    icon: <ShoppingCartIcon className="h-6 w-6" data-id="element-2948" data-mp="3d9b5ci2b9kbt" />,
    title: 'Webshop',
    scope: 'E-commerce, WooCommerce of maatwerk',
    price: 'Op aanvraag',
    priceSuffix: 'incl. betaalkoppeling',
    details: 'Een complete webshop met betalingen, voorraadbeheer en ordermails. Voor bedrijven die producten of diensten direct via de site willen verkopen.'
  }];
  const benefits = [{
    icon: <CheckCircleIcon className="h-6 w-6" data-id="element-2949" data-mp="urmpq2qr1e73" />,
    title: 'Custom design voor jouw merk',
    description: 'Volledig op maat ontworpen, met aandacht voor je doelgroep en de manier waarop jij wilt overkomen.'
  }, {
    icon: <UsersIcon className="h-6 w-6" data-id="element-2950" data-mp="19mswwt1kzj20" />,
    title: 'Specialist in zakelijke dienstverlening',
    description: 'Ervaring met websites voor financieel adviseurs, administratiekantoren, juristen en M&A specialisten.'
  }, {
    icon: <TrendingUpIcon className="h-6 w-6" data-id="element-2951" data-mp="gvrdugurp3d7" />,
    title: 'Gebruiksvriendelijk en helder',
    description: 'Intuitieve navigatie en een heldere structuur, zodat bezoekers snel vinden waar ze voor komen.'
  }, {
    icon: <ShieldIcon className="h-6 w-6" data-id="element-2952" data-mp="3pdx7mvjww10z" />,
    title: 'Professionele uitstraling',
    description: 'Past bij sectoren waar vertrouwen alles is. Geen schreeuwerige opmaak, wel een verzorgde, premium look.'
  }, {
    icon: <StarIcon className="h-6 w-6" data-id="element-2953" data-mp="2bgci75ioogk1" />,
    title: 'Klaar voor SEO en groei',
    description: 'Vanaf dag één geschikt voor SEO-optimalisatie en uitbreiding wanneer je markt erom vraagt.'
  }];
  const topCases = [{
    name: 'Alex Construct',
    sector: 'Renovatie',
    image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=800&h=500&fit=crop'
  }, {
    name: 'Mijn Bedrijfs Waarderingen',
    sector: 'Financiële Dienstverlening',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=500&fit=crop'
  }, {
    name: 'GymFit de Noord',
    sector: 'Crossfit',
    image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&h=500&fit=crop'
  }];
  const timelineSteps = [{
    icon: <MessageCircleIcon className="h-6 w-6" data-id="element-2954" data-mp="19uehrdhzxhwa" />,
    title: 'Intake',
    description: 'We brengen je doelen, doelgroep en wensen helder in kaart via een persoonlijk gesprek.'
  }, {
    icon: <PenToolIcon className="h-6 w-6" data-id="element-2955" data-mp="34xsyt6d92fhe" />,
    title: 'Ontwerp',
    description: 'We maken een eerste ontwerp dat past bij jouw organisatie en je sector.'
  }, {
    icon: <UsersIcon className="h-6 w-6" data-id="element-2956" data-mp="2v3khleto68yj" />,
    title: 'Afstemmen',
    description: 'Je geeft feedback, wij verfijnen. Dit herhalen we tot het echt goed voelt.'
  }, {
    icon: <CodeIcon className="h-6 w-6" data-id="element-2957" data-mp="2m3np1245spy7" />,
    title: 'Bouwen',
    description: 'Is het ontwerp akkoord? Dan bouwen we de website technisch op, snel, veilig en schaalbaar.'
  }, {
    icon: <TestTubeIcon className="h-6 w-6" data-id="element-2958" data-mp="2c8m28cxcvzys" />,
    title: 'Testen en finetunen',
    description: 'Je bekijkt de website en geeft feedback. Wij passen aan tot alles klopt.'
  }, {
    icon: <RocketIcon className="h-6 w-6" data-id="element-2959" data-mp="adbmdpiys6tx" />,
    title: 'Oplevering',
    description: 'Bij oplevering geven we live uitleg over hoe je met je website en systemen werkt.'
  }, {
    icon: <ServerIcon className="h-6 w-6" data-id="element-2960" data-mp="1zugwx8ghstrc" />,
    title: 'Hosting en onderhoud',
    description: 'Na oplevering zorgen wij voor hosting, onderhoud en beveiliging, op onze eigen omgeving.'
  }];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-2961" data-mp="24vmrzcxmkt41">
      <SEO title="Website laten maken vanaf €499 | Noord Digital" description="Onepager vanaf €499, maatwerk en webshops op aanvraag. Professionele websites voor zakelijke dienstverleners, met hosting, e-mail en SEO onder één dak." keywords="website laten maken, onepager 499, maatwerk website, webshop laten maken, zakelijke website, professionele website, custom webdesign" canonical="https://noorddigital.nl/websites" jsonLd={[buildServiceSchema({
      name: 'Websites',
      description: 'Professionele websites voor ondernemers in Noord-Holland Noord, gebouwd om gevonden te worden en vertrouwen te wekken.',
      path: '/websites',
      serviceType: 'Webdesign en websiteontwikkeling'
    }), buildBreadcrumbList([{
      name: 'Websites',
      path: '/websites'
    }])]} data-id="element-2962" data-mp="24ubkp34z1386" />
      <Header data-id="element-2963" data-mp="37ads1mf5xnhe" />

      {/* Hero */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20 relative overflow-hidden" data-id="element-2964" data-mp="37bf8y7iv1471">
        <div className="absolute inset-0 opacity-20" data-id="element-2965" data-mp="3c7e022vkp0b5">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-2966" data-mp="3h70eugr1uom2"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-2967" data-mp="3pzk811oko0zi">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-2968" data-mp="3v0rcaqriai1f">
            <div className="lg:col-span-7" data-id="element-2969" data-mp="3svohr6fb2ms">
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-2970" data-mp="3tong5z54nud">
                Website laten maken, vanaf €499 voor een onepager
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-white text-opacity-90 mb-8 text-lg leading-relaxed" data-id="element-2971" data-mp="3w49y9uo9h80d">
                Van een strakke onepager tot een volwaardige maatwerk website of webshop. We bouwen voor zakelijke dienstverleners die online net zo overtuigend willen overkomen als in een persoonlijk gesprek. Inclusief hosting, e-mail en SEO als je dat wilt.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-wrap gap-4" data-id="element-2972" data-mp="1anurpmuepqiz">
                <motion.a href="#keuze" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg group" data-id="element-2973" data-mp="1z3wlrymcts98">
                  Bekijk de opties
                  <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2974" data-mp="2d0a6dgm6q9rx" />
                </motion.a>
              </motion.div>
            </div>

            <div className="lg:col-span-5" data-id="element-2975" data-mp="233ozepqluolg">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-white rounded-2xl p-8 shadow-2xl relative" data-id="element-2976" data-mp="282icwi5w6flm">
                <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 4,
                ease: 'easeInOut'
              }} className="absolute -top-3 -right-3 w-16 h-16 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full flex items-center justify-center shadow-lg border-4 border-white" data-id="element-2977" data-mp="3avnjqhqpqlng">
                  <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte begeleidt je website" className="w-full h-full object-cover rounded-full" data-id="element-2978" data-mp="3fv8ml3907e5g" />
                </motion.div>
                <div className="text-center mb-6" data-id="element-2979" data-mp="30zarzpeqahvc">
                  <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-2" data-id="element-2980" data-mp="2w5ooonugfqc7">
                    Onepager vanaf
                  </p>
                  <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-2981" data-mp="28f2fgt8ps00m">
                    <span className="text-6xl md:text-7xl font-bold text-[#111827]" data-id="element-2982" data-mp="24fbi26oomln4">
                      €499
                    </span>
                  </div>
                  <p className="text-[#6B7280] text-sm" data-id="element-2983" data-mp="1xun3wyfqxpql">eenmalig, excl. BTW</p>
                </div>

                <div className="space-y-3 pt-6 border-t border-gray-200 mb-6" data-id="element-2984" data-mp="1tsia9zpast3y">
                  <div className="flex items-center gap-3" data-id="element-2985" data-mp="1ougq1bg4ynqw">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-2986" data-mp="1otx65p6xdpa8" />
                    <span className="text-sm text-[#6B7280]" data-id="element-2987" data-mp="1jwoso1wq29fb">
                      Custom design op jouw merk
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-2988" data-mp="1a4iz4nqtnjy0">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-2989" data-mp="1a2y3eusrtuyk" />
                    <span className="text-sm text-[#6B7280]" data-id="element-2990" data-mp="161mbgimfcxn7">
                      Contactformulier en CTA gerichte opbouw
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-2991" data-mp="w6ud3cl0q6rd">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-2992" data-mp="37t4v8554789h" />
                    <span className="text-sm text-[#6B7280]" data-id="element-2993" data-mp="3crzdn5z66ut2">
                      Klaar voor SEO en uitbreiding
                    </span>
                  </div>
                </div>

                <a href="/prijsscan" className="block w-full bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg text-center shadow-md" data-id="element-2994" data-mp="3hp80hjv9jn0n">
                  <span className="flex items-center justify-center" data-id="element-2995" data-mp="2p0k7110d1rc0">
                    Start de website scan
                    <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-2996" data-mp="2y33annbz07zb" />
                  </span>
                </a>
                <p className="text-xs text-[#6B7280] text-center mt-4" data-id="element-2997" data-mp="32tjo37fsjtlo">
                  Duurt 2 minuten, helemaal vrijblijvend
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Website types met prijs */}
      <div id="keuze" className="w-full bg-white py-20 md:py-24" data-id="element-2998" data-mp="2abe9dhh68968">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2999" data-mp="3dn3k52jpcsxj">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3000" data-mp="38q3c4m3alg3s">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-3001" data-mp="3ne7yq15gubru">
              Kies het type website dat bij je past
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3002" data-mp="2pqz51ivb9wer">
              Van een compacte onepager tot een uitgebreide maatwerk website. Drie startpunten, allemaal met dezelfde aandacht voor design en techniek.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3003" data-mp="f0qupjy4iigg">
            {websiteTypes.map((type, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -5,
            borderColor: '#007AFF'
          }} className={`relative rounded-2xl p-8 transition-all duration-300 border-2 group ${type.highlight ? 'bg-white border-[#007AFF] shadow-lg' : 'bg-[#F7F8FA] border-transparent hover:shadow-lg'}`} data-id="element-3004" data-mp="rgqogcpgvywq" data-mpi={index}>
                {type.highlight && <div className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-[#007AFF] text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full shadow-md" data-id="element-3005" data-mp="10g3xx5dlcnhg">
                    <StarIcon className="h-3 w-3" data-id="element-3006" data-mp="1syratxifow7g" />
                    Populair
                  </div>}
                <motion.div whileHover={{
              scale: 1.1,
              rotate: 5
            }} className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-3007" data-mp="57l69ds544mh">
                  {type.icon}
                </motion.div>
                <h3 className="font-bold text-[#111827] text-xl mb-1" data-id="element-3008" data-mp="3mjm7x89sj6tl">
                  {type.title}
                </h3>
                <p className="text-[#007AFF] font-semibold text-sm mb-4" data-id="element-3009" data-mp="j12psyktthci">
                  {type.scope}
                </p>
                <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-3010" data-mp="e9udnus43hvn">
                  <p className="text-3xl font-bold text-[#111827] mb-1" data-id="element-3011" data-mp="e7zuom0ehzu7">
                    {type.price}
                  </p>
                  <p className="text-xs text-[#6B7280]" data-id="element-3012" data-mp="16r6vdbhkrr65">{type.priceSuffix}</p>
                </div>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-6" data-id="element-3013" data-mp="11zezclfngt8m">
                  {type.details}
                </p>
                <a href="/contact" className={`inline-flex items-center justify-center w-full font-semibold py-3 px-4 rounded-lg text-sm transition ${type.highlight ? 'bg-[#007AFF] text-white shadow-md' : 'bg-white text-[#007AFF] border border-[#007AFF]'}`} data-id="element-3014" data-mp="1a9w43r6au44b">
                  Vraag dit type aan
                  <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-3015" data-mp="1t5m9lodfximy" />
                </a>
              </motion.div>)}
          </div>

          <div className="max-w-3xl mx-auto mt-10 bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-6 text-center" data-id="element-3016" data-mp="21z8tag4divfs">
            <p className="text-[#6B7280] leading-relaxed" data-id="element-3017" data-mp="26xiogqq3lw1e">
              Twijfel je tussen een onepager en een volwaardige website? Doe de website scan, dan kijken we samen welk type bij jouw doelen past.
            </p>
          </div>
        </div>
      </div>

      {/* Werkwijze timeline */}
      <div id="werkwijze" className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-3018" data-mp="1r9nlrcq31l29">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3019" data-mp="1mbcbj0vvl8ji">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3020" data-mp="20zqnc1b9s7c7">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-3021" data-mp="1w7ys3dh65dfq">
              Onze werkwijze: duidelijk, persoonlijk en zonder haast
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3022" data-mp="2jw8ccayqht4r">
              We werken stap voor stap. Ontwerp en afstemmen herhalen we net zo vaak tot je volledig tevreden bent.
            </motion.p>
          </div>

          <div className="relative max-w-4xl mx-auto" data-id="element-3023" data-mp="2w9vk8fh78f2p">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#007AFF] bg-opacity-20 hidden md:block" data-id="element-3024" data-mp="315u2rmfuj2v7">
              <motion.div initial={{
              scaleY: 0
            }} whileInView={{
              scaleY: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 1.5,
              ease: 'easeInOut'
            }} className="w-full bg-[#007AFF] origin-top" style={{
              height: '100%'
            }} data-id="element-3025" data-mp="2mhpiwf524lau" />
            </div>

            <div className="space-y-12" data-id="element-3026" data-mp="23jn2upuhlyp6">
              {timelineSteps.map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.15
            }} className="relative flex items-center gap-8 md:gap-12" data-id="element-3027" data-mp="28hoj730j3jwy" data-mpi={index}>
                  <motion.div whileHover={{
                scale: 1.2
              }} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-[#007AFF] rounded-full items-center justify-center text-white shadow-lg hidden md:flex z-10" data-id="element-3028" data-mp="2pol7bucj8px6">
                    {step.icon}
                  </motion.div>
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:ml-auto md:text-left' : 'md:mr-auto md:text-right'}`} data-id="element-3029" data-mp="26z50kpa9818b">
                    <motion.div whileHover={{
                  y: -5,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                }} className="bg-white rounded-xl p-6 shadow-md transition-all duration-300" data-id="element-3030" data-mp="1xyztyhcvx6v7">
                      <div className="flex items-center gap-3 mb-3 md:hidden" data-id="element-3031" data-mp="3nd4zpy19zmui">
                        <div className="w-10 h-10 bg-[#007AFF] rounded-full flex items-center justify-center text-white flex-shrink-0" data-id="element-3032" data-mp="3nb1zymabmfcl">
                          {step.icon}
                        </div>
                        <h3 className="font-bold text-[#111827] text-lg" data-id="element-3033" data-mp="3o9t7sk08u95">
                          {step.title}
                        </h3>
                      </div>
                      <h3 className="font-bold text-[#111827] text-lg mb-2 hidden md:block" data-id="element-3034" data-mp="deurtfzohys2">
                        {step.title}
                      </h3>
                      <p className="text-[#6B7280] leading-relaxed" data-id="element-3035" data-mp="3gwmmj2578wty">
                        {step.description}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>)}
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5
        }} className="text-center mt-12" data-id="element-3036" data-mp="3pvghko2vtf5e">
            <motion.a href="/hosting" whileHover={{
            x: 5
          }} className="inline-flex items-center text-[#007AFF] font-medium" data-id="element-3037" data-mp="165ubp3ijb5vx">
              Bekijk onze hosting (€34,99 per maand)
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-3038" data-mp="1exw88qphg20x" />
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Waarom maatwerk */}
      <div className="w-full bg-white py-20 md:py-24" data-id="element-3039" data-mp="n6oodvqc4486">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3040" data-mp="n89l9s04fz97">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3041" data-mp="r8a9g4d0votg">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-3042" data-mp="w6bq6imz7c1r">
              Waarom een maatwerk website?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3043" data-mp="2ck20w88loykd">
              Een maatwerk website biedt de flexibiliteit en professionaliteit die jouw bedrijf nodig heeft. Geen template, wel een verhaal dat past bij jouw markt.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-3044" data-mp="3ed7axzj0mgq0">
            {benefits.map((benefit, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -5,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
          }} className="bg-[#F7F8FA] rounded-2xl p-6 transition-all duration-300 group" data-id="element-3045" data-mp="39c0360gvhhjc" data-mpi={index}>
                <motion.div whileHover={{
              scale: 1.1,
              rotate: 5
            }} className="w-12 h-12 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-3046" data-mp="2s4imvoo9631m">
                  {benefit.icon}
                </motion.div>
                <h3 className="font-bold text-[#111827] text-lg mb-2" data-id="element-3047" data-mp="2n4w8hbwougr8">
                  {benefit.title}
                </h3>
                <p className="text-[#6B7280] leading-relaxed text-sm" data-id="element-3048" data-mp="3augpvqd715ye">
                  {benefit.description}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-8 max-w-3xl mx-auto" data-id="element-3049" data-mp="31uldxjbtd6xb">
            <h3 className="font-bold text-[#111827] text-xl mb-4 text-center" data-id="element-3050" data-mp="1ugiaux9ihmg7">
              Compleet pakket bij één partij
            </h3>
            <p className="text-[#6B7280] text-center mb-6" data-id="element-3051" data-mp="1mvxtst4fvvur">
              Een professionele website is meer dan alleen design. Combineer met hosting, e-mail en SEO.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-3052" data-mp="1wvf11ezcahsu">
              <motion.a href="/hosting" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-sm group" data-id="element-3053" data-mp="1wssr58oz7q7g">
                Hosting voor €34,99 per maand
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3054" data-mp="1dwj621fnpzdy" />
              </motion.a>
              <motion.a href="/email" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-sm group" data-id="element-3055" data-mp="193zwdfk3boef">
                Zakelijke e-mail vanaf €1,99
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3056" data-mp="3ft38za0rj1vl" />
              </motion.a>
              <motion.a href="/seo" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-sm group" data-id="element-3057" data-mp="3ov2rk6rfee0n">
                SEO optimalisatie
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3058" data-mp="7e1i7pdcktww" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Norden begeleiding */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-20 md:py-24" data-id="element-3059" data-mp="gezq4iqwplkr">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3060" data-mp="2d9vv7gcbbqnw">
          <div className="max-w-4xl mx-auto" data-id="element-3061" data-mp="2i7xdhz1wzlyb">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl" data-id="element-3062" data-mp="2anmrvy0noupy">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-3063" data-mp="2aoxwg01xe6i4">
                <div className="text-center md:text-left" data-id="element-3064" data-mp="25vbt4yhnjeyz">
                  <div className="inline-block relative" data-id="element-3065" data-mp="20xa9ocgbddmz">
                    <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} whileInView={{
                    opacity: 1,
                    scale: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    type: 'spring',
                    stiffness: 100
                  }} className="w-48 h-48 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full flex items-center justify-center p-4 relative overflow-hidden" data-id="element-3066" data-mp="20vqu6o2hf6m9">
                      <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden begeleidt jouw website traject" className="w-full h-full object-cover" data-id="element-3067" data-mp="2floayiy30u1f" />
                    </motion.div>
                    <motion.div animate={{
                    y: [0, -5, 0],
                    rotate: [0, 5, 0]
                  }} transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: 0
                  }} className="absolute -top-2 -right-2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-3068" data-mp="15phqfz940u3q">
                      <CheckCircleIcon className="h-6 w-6 text-[#007AFF]" data-id="element-3069" data-mp="myia1mfoksm2" />
                    </motion.div>
                    <motion.div animate={{
                    y: [0, -5, 0],
                    rotate: [0, -5, 0]
                  }} transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: 1
                  }} className="absolute -bottom-2 -left-2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-3070" data-mp="rpgsoso562sh">
                      <UsersIcon className="h-6 w-6 text-[#007AFF]" data-id="element-3071" data-mp="3zw8y7k5z1i5" />
                    </motion.div>
                  </div>
                </div>
                <div data-id="element-3072" data-mp="3lhs0ly1shzhu">
                  <motion.h2 initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} className="font-bold text-[#111827] text-3xl mb-4" data-id="element-3073" data-mp="3lh9wygcssizw">
                    Norden begeleidt jouw traject
                  </motion.h2>
                  <motion.p initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.1
                }} className="text-[#6B7280] text-lg leading-relaxed mb-6" data-id="element-3074" data-mp="3plrba00e243a">
                    Onze mascotte Norden staat symbool voor hoe wij werken. Als team, samen met jou, naar het gewenste resultaat. Van eerste gesprek tot oplevering staan wij naast je.
                  </motion.p>
                  <div className="space-y-3" data-id="element-3075" data-mp="m3g77lja9pyz">
                    <div className="flex items-center gap-3" data-id="element-3076" data-mp="m4ralk8tgvpk">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3077" data-mp="1tg5en301rcis">
                        <CheckIcon className="h-4 w-4 text-white" data-id="element-3078" data-mp="1xfe43vo20o8j" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-3079" data-mp="22hd6p3byzh4f">
                        Persoonlijke begeleiding door het hele traject
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-3080" data-mp="19sfrj93220fw">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3081" data-mp="1eqhbdw8b23sf">
                        <CheckIcon className="h-4 w-4 text-white" data-id="element-3082" data-mp="1jp2attfqgjft" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-3083" data-mp="1ogsqmexfvzem">
                        Directe communicatie met het ontwikkelteam
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-3084" data-mp="vzzvwtchh4p0">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3085" data-mp="vyf2j71wnrsq">
                        <CheckIcon className="h-4 w-4 text-white" data-id="element-3086" data-mp="oee46lub4tmd" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-3087" data-mp="jfaz51ebunee">
                        Doorwerken tot je volledig tevreden bent
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cases */}
      <div className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-3088" data-mp="qwaulve9nha">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3089" data-mp="pbgaw92y4jf">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3090" data-mp="3ry8tz6fu8azu">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-3091" data-mp="3my48f202oq2r">
              Recent opgeleverd
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3092" data-mp="1dxrbsqu5vix1">
              Een paar van de laatste websites die we hebben opgeleverd. Bekijk de complete portfolio voor meer voorbeelden.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-3093" data-mp="v8b51lrvuu86">
            {topCases.map((clientCase, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -10
          }} className="bg-white rounded-2xl overflow-hidden shadow-md transition-all duration-300 group" data-id="element-3094" data-mp="q6c56lvcqffz" data-mpi={index}>
                <div className="relative h-48 overflow-hidden" data-id="element-3095" data-mp="7g4dr110ldfm">
                  <motion.img whileHover={{
                scale: 1.1
              }} transition={{
                duration: 0.5
              }} src={clientCase.image} alt={`${clientCase.name} website door Noord Digital`} className="w-full h-full object-cover" data-id="element-3096" data-mp="3thtpu7cblmo7" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-3097" data-mp="3ohyxdzcwcp11"></div>
                  <div className="absolute bottom-4 left-4 right-4" data-id="element-3098" data-mp="g2nnnb2uta65">
                    <span className="inline-block bg-[#007AFF] text-white text-xs font-medium px-3 py-1 rounded-full mb-2" data-id="element-3099" data-mp="l32ynvzny5ky">
                      {clientCase.sector}
                    </span>
                    <h3 className="text-white font-bold text-lg" data-id="element-3100" data-mp="b3u5v6gjax06">
                      {clientCase.name}
                    </h3>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="text-center" data-id="element-3101" data-mp="2vt9tjskgi5ll">
            <motion.a href="/portfolio" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="inline-flex items-center bg-[#007AFF] text-white font-semibold py-3 px-8 rounded-lg shadow-md group" data-id="element-3102" data-mp="smc4xgcvho5s">
              Bekijk alle cases
              <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3103" data-mp="em9ps1vy98u8" />
            </motion.a>
          </motion.div>
        </div>
      </div>

      <Footer data-id="element-3104" data-mp="4pombkzkw5si" />
    </div>;
};